import React from 'react';
import ImageGallery from 'react-image-gallery';
import "react-image-gallery/styles/css/image-gallery.css";
import { Box, IconButton, Dialog, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

const isVideoSource = (value = '') => {
    const v = String(value || '').toLowerCase();
    return (
        /\.(mp4|webm|ogg)(\?.*)?$/i.test(v) ||
        v.includes('youtube') ||
        v.includes('vimeo')
    );
};

const isImageThumbCandidate = (value = '') => {
    const v = String(value || '').trim();
    if (!v) return false;
    if (v.startsWith('data:image/')) return true;
    return !isVideoSource(v);
};

const NewsGallery = ({ gallery, image, title, variant = 'default' }) => {
    const [lightboxOpen, setLightboxOpen] = React.useState(false);
    const [desktopIndex, setDesktopIndex] = React.useState(0);
    const [lightboxIndex, setLightboxIndex] = React.useState(0);
    const [desktopViewportH, setDesktopViewportH] = React.useState(() =>
        typeof window !== 'undefined' ? window.innerHeight : 900
    );
    const [desktopContainerW, setDesktopContainerW] = React.useState(960);
    const [mobileIndex, setMobileIndex] = React.useState(0);
    const [mobileAutoPlay] = React.useState(true);
    const [mobileViewerOpen, setMobileViewerOpen] = React.useState(false);
    const [showGestureHint, setShowGestureHint] = React.useState(true);
    const [viewerScale, setViewerScale] = React.useState(1);
    const [viewerOffset, setViewerOffset] = React.useState({ x: 0, y: 0 });
    const [viewerDragX, setViewerDragX] = React.useState(0);
    const [viewerDragY, setViewerDragY] = React.useState(0);
    const [viewerUiVisible, setViewerUiVisible] = React.useState(true);
    const mainGalleryRef = React.useRef(null);
    const galleryShellRef = React.useRef(null);
    const viewerFrameRef = React.useRef(null);
    const gestureRef = React.useRef({
        mode: null,
        startDistance: 0,
        startScale: 1,
        startPoint: null,
        startOffset: { x: 0, y: 0 },
        lastTapAt: 0
    });
    const touchStartXRef = React.useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isLandscape = useMediaQuery('(orientation: landscape)');

    // Nav buttons need to be easy on touch screens and avoid "edge gestures" (Safari/Chrome back swipe).
    // Keep a larger hit target while keeping the visual compact.
    const navInset = { xs: '14px', sm: '16px' };

    const baseItems = React.useMemo(() => {
        if (gallery && Array.isArray(gallery) && gallery.length > 0) return gallery;
        if (image) return [{ original: image, thumbnail: image }];
        return [];
    }, [gallery, image]);

    const normalizedBaseItems = React.useMemo(() => {
        return baseItems.map((item) => {
            const url = item.original || item.url || item;
            const isVideo = isVideoSource(url);
            const caption =
                typeof item === 'object' && item !== null
                    ? (item.caption || item.legend || item.description || item.alt || item.title || '')
                    : '';
            const rawThumb =
                typeof item === 'object' && item !== null
                    ? (item.thumbnail || item.poster || item.previewImage || item.preview || item.image || '')
                    : '';
            const thumbnailUrl = isImageThumbCandidate(rawThumb) ? rawThumb : '';
            return { url, isVideo, caption, thumbnailUrl };
        });
    }, [baseItems]);

    const defaultVideoThumbUrl = React.useMemo(() => {
        const firstImage = normalizedBaseItems.find((item) => !item.isVideo && isImageThumbCandidate(item.thumbnailUrl || item.url));
        if (firstImage) return firstImage.thumbnailUrl || firstImage.url;
        if (isImageThumbCandidate(image)) return image;
        return '/images/banner-overview-new.jpg';
    }, [normalizedBaseItems, image]);

    const mobileItems = React.useMemo(() => {
        return normalizedBaseItems.map((item) => ({ url: item.url, isVideo: item.isVideo }));
    }, [normalizedBaseItems]);

    React.useEffect(() => {
        setMobileIndex(0);
        setDesktopIndex(0);
        setLightboxIndex(0);
    }, [gallery, image, title, variant]);

    React.useEffect(() => {
        if (!isMobile) return;
        const t = setTimeout(() => setShowGestureHint(false), 3600);
        return () => clearTimeout(t);
    }, [isMobile]);

    React.useEffect(() => {
        if (isMobile || typeof window === 'undefined') return;
        const handleResize = () => {
            setDesktopViewportH(window.innerHeight || 900);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobile]);

    React.useEffect(() => {
        if (isMobile) return;
        const node = galleryShellRef.current;
        if (!node) return;

        const updateWidth = () => {
            setDesktopContainerW(node.clientWidth || 960);
        };
        updateWidth();

        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(node);
        return () => observer.disconnect();
    }, [isMobile, variant]);

    React.useEffect(() => {
        setViewerScale(1);
        setViewerOffset({ x: 0, y: 0 });
        setViewerDragX(0);
        setViewerDragY(0);
        setViewerUiVisible(true);
    }, [mobileIndex, mobileViewerOpen]);

    const closeMobileViewer = React.useCallback(() => {
        setMobileViewerOpen(false);
        setViewerScale(1);
        setViewerOffset({ x: 0, y: 0 });
        setViewerDragX(0);
        setViewerDragY(0);
        setViewerUiVisible(true);
    }, []);

	    const galleryItems = React.useMemo(() => {
		        return normalizedBaseItems.map(({ url, isVideo, caption, thumbnailUrl }, index) => {

            if (isVideo) {
                const shouldAutoplayMuted = variant === 'hero';
                return {
                    original: url,
                    // react-image-gallery expects an image thumbnail; a video URL will show as broken.
                    thumbnail: thumbnailUrl || defaultVideoThumbUrl,
                    embedUrl: url,
                    isVideo: true,
                    caption,
                    renderItem: () => (
                        <Box
                            data-media-index={index}
                            sx={{
                                width: '100%',
                                height: 'var(--gallery-max-h)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#000',
                            }}
                        >
	                            <video
	                                src={url}
	                                className="gallery-media"
                                    controls={!shouldAutoplayMuted}
                                    muted={shouldAutoplayMuted}
                                    autoPlay={shouldAutoplayMuted}
                                    loop={shouldAutoplayMuted}
	                                preload={shouldAutoplayMuted ? 'auto' : 'metadata'}
	                                playsInline
                                    poster={thumbnailUrl || defaultVideoThumbUrl}
                                    onCanPlay={(event) => {
                                        if (!shouldAutoplayMuted) return;
                                        event.currentTarget.play().catch(() => {});
                                    }}
	                                style={{
	                                    maxWidth: '100%',
	                                    maxHeight: '100%',
	                                    height: '100%',
                                    width: '100%',
                                    objectFit: shouldAutoplayMuted ? 'cover' : 'contain',
                                }}
                            />
                        </Box>
                    ),
                };
            }

            // Evidence window: deep black, no cinematic blur.
            return {
                original: url,
                thumbnail: thumbnailUrl || url,
                isVideo: false,
                caption,
                renderItem: () => (
                    <Box
                        data-media-index={index}
                        sx={{
                            width: '100%',
                            height: 'var(--gallery-max-h)',
                            bgcolor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        {/* When using `contain`, avoid ugly black bars by using a blurred cover background. */}
                        {variant !== 'hero' && (
                            <Box
                                aria-hidden="true"
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundImage: `url(${url})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    filter: 'blur(14px)',
                                    transform: 'scale(1.08)',
                                    opacity: 0.24,
                                }}
                            />
                        )}
                        <Box
                            component="img"
                            className="gallery-media"
                            src={url}
                            alt={title || "Notícia"}
                            sx={{ display: 'block', position: 'relative', zIndex: 1 }}
                            style={{
                                width: '100%',
                                height: '100%',
                                // Hero should be edge-to-edge (no side gaps).
                                // Bottom gallery stays evidence-style (contain).
                                objectFit: variant === 'hero' ? 'cover' : 'contain',
                            }}
                        />
                    </Box>
                ),
	            };
	        });
	    }, [normalizedBaseItems, title, variant, defaultVideoThumbUrl]);

    const lightboxCount = normalizedBaseItems.length;

    const showThumbnails = galleryItems.length > 1 && variant !== 'hero';
    const showNav = galleryItems.length > 1;
    const showEditorialTrail = false;
    const mediaMaxH = React.useMemo(() => {
        if (isMobile) {
            return variant === 'hero' ? 'clamp(220px, 48vh, 340px)' : 'clamp(240px, 52vh, 420px)';
        }

        // Multi-constraint sizing for desktop: adapts to viewport height and container width.
        const isHero = variant === 'hero';
        const hardMin = isHero ? 220 : 200;
        const hardMax = isHero ? 500 : 440;
        const vhBudget = Math.round(desktopViewportH * (isHero ? 0.46 : 0.40));
        const widthBudget = Math.round(desktopContainerW * (isHero ? 0.60 : 0.54));
        const value = Math.max(hardMin, Math.min(hardMax, vhBudget, widthBudget));
        return `${value}px`;
    }, [desktopContainerW, desktopViewportH, isMobile, variant]);

    const showZoom = variant === 'bottom' && !isMobile;
    const desktopItemCount = galleryItems.length;
    const desktopCurrentItem = galleryItems[Math.min(desktopIndex, Math.max(0, desktopItemCount - 1))];
    const desktopCaption = desktopCurrentItem?.caption || '';
    const safeLightboxIndex = Math.max(0, Math.min(lightboxCount - 1, lightboxIndex));
    const lightboxCurrentItem = normalizedBaseItems[safeLightboxIndex];

    const navBtnSx = {
        zIndex: 60,
        color: '#262626',
        bgcolor: 'rgba(255,255,255,0.96)',
        border: '1px solid rgba(0,0,0,0.10)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        '&:hover': { bgcolor: '#fff' },
        '&:active': { bgcolor: '#f7f7f7' },
        width: { xs: 38, sm: 40 },
        height: { xs: 38, sm: 40 },
        borderRadius: '50%',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        pointerEvents: 'auto',
        '&:focus-visible': {
            outline: '3px solid #cc0000',
            outlineOffset: '2px',
        },
        // Make disabled state feel deliberate (instead of "button is broken").
        '&.Mui-disabled': {
            opacity: 0.45,
            color: 'rgba(0,0,0,0.55)',
        },
    };

    const bindNavTouch = (onClick) => ({
        onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick(e);
        },
        onTouchEnd: (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick(e);
        },
    });

    const goDesktopToIndex = React.useCallback((index) => {
        if (!showNav) return;
        if (mainGalleryRef.current?.slideToIndex) {
            mainGalleryRef.current.slideToIndex(index);
        }
        setDesktopIndex(index);
    }, [showNav]);

    const resolveVisibleDesktopIndex = React.useCallback(() => {
        const refIndex = mainGalleryRef.current?.getCurrentIndex?.();
        if (Number.isInteger(refIndex) && refIndex >= 0 && refIndex < desktopItemCount) {
            return refIndex;
        }
        return Math.max(0, Math.min(desktopItemCount - 1, desktopIndex));
    }, [desktopIndex, desktopItemCount]);

    const goLightboxPrev = React.useCallback(() => {
        if (lightboxCount <= 1) return;
        setLightboxIndex((prev) => (prev - 1 + lightboxCount) % lightboxCount);
    }, [lightboxCount]);

    const goLightboxNext = React.useCallback(() => {
        if (lightboxCount <= 1) return;
        setLightboxIndex((prev) => (prev + 1) % lightboxCount);
    }, [lightboxCount]);

    const handleDesktopKeyDown = React.useCallback((event) => {
        if (!showNav) return;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goDesktopToIndex((desktopIndex - 1 + desktopItemCount) % desktopItemCount);
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            goDesktopToIndex((desktopIndex + 1) % desktopItemCount);
        }
    }, [desktopIndex, desktopItemCount, goDesktopToIndex, showNav]);

    const mobileCanNavigate = mobileItems.length > 1;
    const currentMobileItem = mobileItems[mobileIndex];
    const enableMobileViewer = variant !== 'hero';

    const clampOffset = React.useCallback((x, y, scale) => {
        const rect = viewerFrameRef.current?.getBoundingClientRect();
        if (!rect || scale <= 1) return { x: 0, y: 0 };
        const maxX = ((rect.width * scale) - rect.width) / 2;
        const maxY = ((rect.height * scale) - rect.height) / 2;
        return {
            x: Math.max(-maxX, Math.min(maxX, x)),
            y: Math.max(-maxY, Math.min(maxY, y))
        };
    }, []);
    const goMobilePrev = () => {
        if (!mobileCanNavigate) return;
        setMobileIndex((prev) => (prev - 1 + mobileItems.length) % mobileItems.length);
    };
    const goMobileNext = () => {
        if (!mobileCanNavigate) return;
        setMobileIndex((prev) => (prev + 1) % mobileItems.length);
    };
    const handleMobileTouchStart = (e) => {
        touchStartXRef.current = e.changedTouches?.[0]?.clientX ?? null;
    };
    const handleMobileTouchEnd = (e) => {
        const startX = touchStartXRef.current;
        const endX = e.changedTouches?.[0]?.clientX ?? null;
        touchStartXRef.current = null;
        if (startX == null || endX == null) return;
        const deltaX = endX - startX;
        const threshold = 42;
        if (Math.abs(deltaX) < threshold) {
            const now = Date.now();
            const isDoubleTap = now - gestureRef.current.lastTapAt < 260;
            gestureRef.current.lastTapAt = now;
            if (isDoubleTap && enableMobileViewer && currentMobileItem && !currentMobileItem.isVideo) {
                setMobileViewerOpen(true);
                setShowGestureHint(false);
            }
            return;
        }
        if (deltaX > 0) goMobilePrev();
        else goMobileNext();
    };

    const getTouchDistance = (touches) => {
        const [a, b] = touches;
        const dx = b.clientX - a.clientX;
        const dy = b.clientY - a.clientY;
        return Math.sqrt((dx * dx) + (dy * dy));
    };

    const handleViewerTouchStart = (e) => {
        if (!currentMobileItem || currentMobileItem.isVideo) return;
        const touches = e.touches;
        if (touches.length === 2) {
            gestureRef.current.mode = 'pinch';
            gestureRef.current.startDistance = getTouchDistance(touches);
            gestureRef.current.startScale = viewerScale;
            gestureRef.current.startOffset = viewerOffset;
            return;
        }
        if (touches.length === 1) {
            const now = Date.now();
            if (now - gestureRef.current.lastTapAt < 260) {
                const nextScale = viewerScale > 1 ? 1 : 2.2;
                setViewerScale(nextScale);
                setViewerOffset({ x: 0, y: 0 });
                setViewerDragX(0);
                setViewerDragY(0);
                setViewerUiVisible(true);
            }
            gestureRef.current.lastTapAt = now;
            gestureRef.current.startPoint = { x: touches[0].clientX, y: touches[0].clientY };
            gestureRef.current.startOffset = viewerOffset;

            if (viewerScale > 1) {
                gestureRef.current.mode = 'pan';
            } else {
                gestureRef.current.mode = 'dismiss';
            }
        }
    };

    const handleViewerTouchMove = (e) => {
        if (!currentMobileItem || currentMobileItem.isVideo) return;
        const touches = e.touches;
        if (gestureRef.current.mode === 'pinch' && touches.length === 2) {
            e.preventDefault();
            const nextDistance = getTouchDistance(touches);
            const rawScale = gestureRef.current.startScale * (nextDistance / Math.max(1, gestureRef.current.startDistance));
            const nextScale = Math.max(1, Math.min(4, rawScale));
            setViewerScale(nextScale);
            if (nextScale <= 1.01) {
                setViewerOffset({ x: 0, y: 0 });
            } else {
                setViewerOffset((prev) => clampOffset(prev.x, prev.y, nextScale));
            }
            return;
        }

        if (gestureRef.current.mode === 'pan' && touches.length === 1 && viewerScale > 1) {
            e.preventDefault();
            const t = touches[0];
            const dx = t.clientX - gestureRef.current.startPoint.x;
            const dy = t.clientY - gestureRef.current.startPoint.y;
            const raw = {
                x: gestureRef.current.startOffset.x + dx,
                y: gestureRef.current.startOffset.y + dy
            };
            setViewerOffset(clampOffset(raw.x, raw.y, viewerScale));
            return;
        }

        if (gestureRef.current.mode === 'dismiss' && touches.length === 1 && viewerScale <= 1.01) {
            const t = touches[0];
            const dx = t.clientX - gestureRef.current.startPoint.x;
            const dy = t.clientY - gestureRef.current.startPoint.y;
            if (Math.abs(dx) > Math.abs(dy) * 1.1) {
                e.preventDefault();
                setViewerDragX(Math.max(-180, Math.min(180, dx)));
                setViewerDragY(0);
            } else if (Math.abs(dy) > Math.abs(dx) * 1.1 && dy > 0) {
                e.preventDefault();
                setViewerDragY(Math.min(220, dy * 0.9));
                setViewerDragX(0);
            }
        }
    };

    const handleViewerTouchEnd = () => {
        if (gestureRef.current.mode === 'dismiss') {
            if (Math.abs(viewerDragX) > 72 && mobileCanNavigate) {
                if (viewerDragX < 0) goMobileNext();
                else goMobilePrev();
                setViewerDragX(0);
                setViewerDragY(0);
            } else if (viewerDragY > 92) {
                closeMobileViewer();
            } else {
                setViewerDragX(0);
                setViewerDragY(0);
            }
        }
        gestureRef.current.mode = null;
        gestureRef.current.startPoint = null;
    };

    const handleViewerTap = () => {
        if (viewerScale > 1.01) return;
        setViewerUiVisible((prev) => !prev);
    };

    React.useEffect(() => {
        if (!isMobile || !mobileCanNavigate || !mobileAutoPlay || mobileViewerOpen) return;
        const current = mobileItems[mobileIndex];
        if (current?.isVideo) return;
        const id = setTimeout(() => {
            setMobileIndex((prev) => (prev + 1) % mobileItems.length);
        }, 4600);
        return () => clearTimeout(id);
    }, [isMobile, mobileCanNavigate, mobileAutoPlay, mobileViewerOpen, mobileItems, mobileIndex]);

    if (galleryItems.length === 0) {
        return (
            <Box
                sx={{
                    height: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    borderRadius: '16px',
                }}
                data-ui="widget-news-gallery-empty"
            >
                Sem imagem disponível
            </Box>
        );
    }

    if (isMobile) {
        const current = currentMobileItem;

        return (
            <>
            <Box
                sx={{
                    mb: variant === 'hero' ? 2 : 3,
                    borderRadius: variant === 'hero' ? '12px' : '14px',
                    overflow: 'hidden',
                    bgcolor: '#000',
                    border: '1px solid rgba(255,255,255,0.1)',
                    position: 'relative',
                }}
                data-ui="widget-news-gallery-mobile"
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: variant === 'hero' ? 'clamp(220px, 48vh, 340px)' : 'clamp(240px, 52vh, 420px)',
                        bgcolor: '#000',
                        touchAction: 'pan-y',
                        userSelect: 'none',
                    }}
                    onTouchStart={handleMobileTouchStart}
                    onTouchEnd={handleMobileTouchEnd}
                >
                    {current?.isVideo ? (
                        <video
                            src={current.url}
                            controls={variant !== 'hero'}
                            muted={variant === 'hero'}
                            autoPlay={variant === 'hero'}
                            loop={variant === 'hero'}
                            preload={variant === 'hero' ? 'auto' : 'metadata'}
                            playsInline
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: variant === 'hero' ? 'cover' : 'contain',
                                backgroundColor: '#000',
                            }}
                        />
                    ) : (
                        <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: '#000' }}>
                            <Box
                                aria-hidden="true"
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundImage: `url(${current?.url})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    filter: 'blur(24px)',
                                    transform: 'scale(1.08)',
                                    opacity: 0.42,
                                }}
                            />
                            <img
                                src={current?.url}
                                alt={title || 'Noticia'}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    display: 'block',
                                    backgroundColor: 'transparent',
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            />
                        </Box>
                    )}

                    {mobileCanNavigate && (
                        <>
                            <Box
                                onClick={goMobilePrev}
                                aria-label="Imagem anterior"
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '34%',
                                    zIndex: 4,
                                }}
                            />
                            <Box
                                onClick={goMobileNext}
                                aria-label="Próxima imagem"
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '34%',
                                    zIndex: 4,
                                }}
                            />
                        </>
                    )}

                    {mobileCanNavigate && (
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                top: 20,
                                right: 10,
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                fontWeight: 600,
                                zIndex: 5,
                            }}
                        >
                            {mobileIndex + 1}/{mobileItems.length}
                        </Typography>
                    )}

                    {enableMobileViewer && !current?.isVideo && (
                        <IconButton
                            onClick={() => setMobileViewerOpen(true)}
                            sx={{
                                position: 'absolute',
                                right: 10,
                                bottom: 10,
                                width: 30,
                                height: 30,
                                borderRadius: 1,
                                bgcolor: 'rgba(0,0,0,0.55)',
                                color: '#fff',
                                zIndex: 6,
                            }}
                            aria-label="Ampliar imagem"
                        >
                            <ZoomInIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    )}

                    {showGestureHint && enableMobileViewer && !current?.isVideo && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                bottom: 12,
                                transform: 'translateX(-50%)',
                                px: 1.2,
                                py: 0.55,
                                borderRadius: 1,
                                bgcolor: 'rgba(0,0,0,0.58)',
                                color: '#fff',
                                fontSize: '0.68rem',
                                letterSpacing: '0.01em',
                                zIndex: 6,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Toque no ícone para ampliar
                        </Box>
                    )}
                </Box>

                {mobileCanNavigate && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 1.1, bgcolor: '#0b0b0b' }}>
                        {mobileItems.map((_, index) => (
                            <Box
                                key={`mobile-dot-${index}`}
                                onClick={() => setMobileIndex(index)}
                                sx={{
                                    width: index === mobileIndex ? 14 : 8,
                                    height: index === mobileIndex ? 14 : 8,
                                    borderRadius: 2,
                                    bgcolor: index === mobileIndex ? '#cc0000' : 'rgba(255,255,255,0.35)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
            <Dialog
                fullScreen
                open={mobileViewerOpen && !current?.isVideo}
                onClose={closeMobileViewer}
                PaperProps={{
                    sx: {
                        bgcolor: '#000',
                    }
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        bgcolor: viewerDragY > 0 ? `rgba(0,0,0,${Math.max(0.45, 1 - (viewerDragY / 260))})` : '#000',
                        overflow: 'hidden',
                        transition: 'background-color 220ms ease',
                    }}
                >
                    {viewerUiVisible && (
                        <>
                            <IconButton
                                onClick={closeMobileViewer}
                                sx={{
                                    position: 'absolute',
                                    left: 12,
                                    top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
                                    zIndex: 20,
                                    px: 1.1,
                                    py: 0.75,
                                    borderRadius: '10px',
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    color: '#111',
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    display: 'flex',
                                    gap: 0.5,
                                }}
                                aria-label="Voltar para a notícia"
                            >
                                <ArrowBackIcon sx={{ fontSize: 16 }} />
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
                                    Voltar
                                </Typography>
                            </IconButton>

                            <IconButton
                                onClick={closeMobileViewer}
                                sx={{
                                    position: 'absolute',
                                    right: 12,
                                    top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
                                    zIndex: 20,
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    color: '#111',
                                }}
                                aria-label="Fechar imagem"
                            >
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </>
                    )}

                    <Box
                        ref={viewerFrameRef}
                        onClick={handleViewerTap}
                        onTouchStart={handleViewerTouchStart}
                        onTouchMove={handleViewerTouchMove}
                        onTouchEnd={handleViewerTouchEnd}
                        sx={{
                            width: '100%',
                            height: '100%',
                            touchAction: 'none',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src={current?.url}
                            alt={title || 'Noticia'}
                            style={{
                                width: isLandscape ? '100vw' : '100%',
                                height: '100dvh',
                                objectFit: 'contain',
                                transform: `translate3d(${viewerOffset.x + viewerDragX}px, ${viewerOffset.y + viewerDragY}px, 0) scale(${viewerScale})`,
                                transformOrigin: 'center center',
                                transition: gestureRef.current.mode ? 'none' : 'transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1)',
                                willChange: 'transform',
                                userSelect: 'none',
                            }}
                        />
                    </Box>

                </Box>
            </Dialog>
            </>
        );
    }

    return (
        <>
            <Box
                ref={galleryShellRef}
                sx={{
                    mb: variant === 'hero' ? 3 : 6,
                    borderRadius: variant === 'hero' ? '12px' : '16px',
                    overflow: 'hidden',
                    boxShadow: variant === 'hero' ? 'none' : '0 8px 24px rgba(0,0,0,0.08)',
                    bgcolor: variant === 'hero' ? '#000' : '#fff',
                    border: variant === 'hero' ? '1px solid rgba(0,0,0,0.35)' : '1px solid rgba(0,0,0,0.08)',
                    p: variant === 'hero' ? 0 : { xs: 1.5, sm: 2 },
                    position: 'relative',
                    width: '100%',
                    // Let CSS override use a variable so size adapts per variant.
                    '--gallery-max-h': mediaMaxH,
                }}
                data-ui="widget-news-gallery"
                tabIndex={showEditorialTrail ? 0 : -1}
                onKeyDown={showEditorialTrail ? handleDesktopKeyDown : undefined}
                aria-label={showEditorialTrail ? 'Galeria de mídia. Use as setas esquerda e direita para navegar.' : undefined}
            >
                {variant === 'bottom' && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 1.5,
                            mb: 1.5,
                            px: { xs: 0.25, sm: 0.5 },
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1,
                                    letterSpacing: '0.08em',
                                    fontWeight: 700,
                                }}
                            >
                                Galeria da notícia
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    mt: 0.25,
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    maxWidth: { xs: '70vw', md: '40vw' },
                                }}
                            >
                                {desktopCurrentItem?.isVideo ? 'Vídeo' : 'Imagem'} {Math.min(desktopIndex + 1, desktopItemCount)}/{desktopItemCount}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {showZoom && (
                                <IconButton
                                    onClick={() => {
                                        const currentIndex = resolveVisibleDesktopIndex();
                                        setLightboxIndex(currentIndex);
                                        setDesktopIndex(currentIndex);
                                        if (mainGalleryRef.current?.slideToIndex) {
                                            mainGalleryRef.current.slideToIndex(currentIndex);
                                        }
                                        setLightboxOpen(true);
                                    }}
                                    sx={{
                                        color: '#262626',
                                        bgcolor: 'rgba(255,255,255,0.96)',
                                        border: '1px solid rgba(0,0,0,0.10)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                        '&:hover': { bgcolor: '#fff' },
                                        '&:active': { bgcolor: '#f7f7f7' },
                                        width: { xs: 38, sm: 40 },
                                        height: { xs: 38, sm: 40 },
                                        borderRadius: '50%',
                                        '&:focus-visible': {
                                            outline: '3px solid #cc0000',
                                            outlineOffset: '2px',
                                        },
                                    }}
                                    aria-label="Ampliar"
                                    title="Ampliar"
                                >
                                    <OpenInFullIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                )}

                <ImageGallery
                    ref={mainGalleryRef}
                    items={galleryItems}
                    showPlayButton={false}
                    showFullscreenButton={false}
                    showNav={showNav}
                    showThumbnails={showThumbnails}
                    showBullets={false}
                    autoPlay={false}
                    slideInterval={6000}
                    onBeforeSlide={(index) => setDesktopIndex(index)}
                    onSlide={(index) => setDesktopIndex(index)}
                    onThumbnailClick={(_, index) => {
                        setDesktopIndex(index);
                    }}
                    renderLeftNav={(onClick, disabled) => (
                        <IconButton
                            {...bindNavTouch(onClick)}
                            disabled={disabled}
                            disableRipple
                            sx={{
                                position: 'absolute',
                                left: navInset,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                ...navBtnSx,
                            }}
                            aria-label="Anterior"
                            title="Anterior"
                        >
                            <ArrowBackIcon sx={{ fontSize: isMobile ? 24 : 26 }} />
                        </IconButton>
                    )}
                    renderRightNav={(onClick, disabled) => (
                        <IconButton
                            {...bindNavTouch(onClick)}
                            disabled={disabled}
                            disableRipple
                            sx={{
                                position: 'absolute',
                                right: navInset,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                ...navBtnSx,
                            }}
                            aria-label="Próximo"
                            title="Próximo"
                        >
                            <ArrowBackIcon
                                sx={{ fontSize: isMobile ? 24 : 26, transform: 'rotate(180deg)' }}
                            />
                        </IconButton>
                    )}
                    additionalClass="custom-gallery"
                />
                {variant === 'bottom' && desktopCaption && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            mt: 1.15,
                            px: 0.5,
                            color: 'text.secondary',
                            lineHeight: 1.45,
                            fontSize: '0.76rem',
                        }}
                    >
                        {desktopCaption}
                    </Typography>
                )}
                <style>{`
                    /* Scope to this component only. */
                    .custom-gallery .image-gallery-icon {
                        color: #262626;
                        filter: none;
                    }
                        .custom-gallery .image-gallery-icon:hover {
                            color: #262626; /* no red hover */
                        }
                        .custom-gallery .image-gallery-left-nav,
                        .custom-gallery .image-gallery-right-nav {
                            display: none !important;
                        }

                    /* Neutralize global cinematic rules for images inside this gallery. */
                    .custom-gallery .image-gallery-slide img {
                        border-radius: 14px;
                        box-shadow: none;
                    }

                    /* Reset aggressive height overrides to allow adaptive sizing */
                    .custom-gallery.image-gallery,
                    .custom-gallery .image-gallery-content,
                    .custom-gallery .image-gallery-slide-wrapper,
                    .custom-gallery .image-gallery-swipe,
                    .custom-gallery .image-gallery-slides {
                        height: auto !important;
                    }

                    .custom-gallery .image-gallery-slide {
                        background: #000;
                    }

                    /* Ensure custom renderItems take full width */
                    .custom-gallery .image-gallery-slide > div {
                        width: 100%;
                    }

                    /* Default Media Sizing (Normal View) */
                    .custom-gallery .gallery-media {
                        max-height: var(--gallery-max-h);
                        height: var(--gallery-max-h);
                        width: 100%;
                    }

                    /* Keep the gallery from dominating short desktop viewports. */
                    @media (max-height: 860px) {
                        .custom-gallery .gallery-media {
                            max-height: min(var(--gallery-max-h), 44vh);
                            height: min(var(--gallery-max-h), 44vh);
                        }
                    }
                    @media (max-height: 740px) {
                        .custom-gallery .gallery-media {
                            max-height: min(var(--gallery-max-h), 38vh);
                            height: min(var(--gallery-max-h), 38vh);
                        }
                    }

                    /* If the container is short, avoid thumbnail strip stealing reading space */
                    @media (max-height: 720px) {
                        .custom-gallery .image-gallery-thumbnails-wrapper {
                            display: none;
                        }
                    }

                    /* Hide default image since we are using custom renderItem */
                    .custom-gallery .image-gallery-slide > img {
                        display: none;
                    }

                    /* White editorial frame */
                    .custom-gallery .image-gallery-content {
                        background: transparent !important;
                    }
                    .custom-gallery .image-gallery-slide-wrapper {
                        border-radius: ${variant === 'hero' ? '0px' : '12px'};
                        overflow: hidden;
                        background: #000;
                    }

                    /* Thumbnails: neutral + micro-red signature only on active */
                    .custom-gallery .image-gallery-thumbnails-wrapper {
                        margin-top: 10px;
                        background: transparent;
                    }
                    .custom-gallery .image-gallery-thumbnail {
                        border-radius: 10px;
                        border: 1px solid rgba(0,0,0,0.10);
                        background: #fff;
                        transition: opacity 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
                        width: 88px;
                        opacity: 0.85;
                        position: relative;
                        overflow: hidden;
                    }
                    .custom-gallery .image-gallery-thumbnail:hover {
                        opacity: 1;
                        border-color: rgba(0,0,0,0.16);
                    }
                    .custom-gallery .image-gallery-thumbnail:focus-visible {
                        outline: 3px solid #cc0000;
                        outline-offset: 2px;
                    }
                    .custom-gallery .image-gallery-thumbnail.active {
                        opacity: 1;
                        border: 2px solid #cc0000;
                        box-shadow: 0 1px 4px rgba(0,0,0,0.10);
                    }
                    .custom-gallery .image-gallery-thumbnail.active::after {
                        content: none;
                    }
                    .custom-gallery .image-gallery-thumbnail-image {
                        height: 58px;
                        object-fit: cover;
                        object-position: center;
                    }
                `}</style>
            </Box>

            <Dialog
                fullScreen={isMobile}
                open={lightboxOpen && showZoom}
                onClose={() => {
                    setLightboxOpen(false);
                    setDesktopIndex(safeLightboxIndex);
                    if (mainGalleryRef.current?.slideToIndex) {
                        mainGalleryRef.current.slideToIndex(safeLightboxIndex);
                    }
                }}
                fullWidth
                maxWidth="xl"
                sx={{
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        backdropFilter: 'blur(10px)',
                    },
                    '& .MuiDialog-container': {
                        alignItems: 'center',
                    },
                }}
                PaperProps={{
                    sx: {
                        width: { xs: '94vw', md: 'min(1180px, 92vw)' },
                        height: { xs: '78vh', md: '84vh' },
                        borderRadius: '24px',
                        overflow: 'hidden',
                        bgcolor: '#0b0b0b',
                        backgroundImage:
                            'radial-gradient(circle at 12% 12%, rgba(204,0,0,0.10) 0%, transparent 46%), radial-gradient(circle at 90% 20%, rgba(255,255,255,0.05) 0%, transparent 52%)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }
                }}
            >
                <Box sx={{ position: 'relative', width: '100%', height: '100%', p: { xs: 1.25, sm: 2 }, '--gallery-max-h': '100%' }}>
                    <IconButton
                        onClick={() => {
                            setLightboxOpen(false);
                            setDesktopIndex(safeLightboxIndex);
                            if (mainGalleryRef.current?.slideToIndex) {
                                mainGalleryRef.current.slideToIndex(safeLightboxIndex);
                            }
                        }}
                        sx={{
                            position: 'absolute',
                            right: { xs: 10, sm: 12 },
                            top: { xs: 10, sm: 12 },
                            zIndex: 50,
                            color: '#262626',
                            bgcolor: 'rgba(255,255,255,0.96)',
                            border: '1px solid rgba(0,0,0,0.10)',
                            '&:hover': { bgcolor: '#fff' },
                            '&:active': { bgcolor: '#f7f7f7' },
                            width: { xs: 38, sm: 40 },
                            height: { xs: 38, sm: 40 },
                            borderRadius: '50%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            '&:focus-visible': {
                                outline: '3px solid #cc0000',
                                outlineOffset: '2px',
                            },
                        }}
                        aria-label="Fechar"
                        title="Fechar"
	                    >
	                        <CloseIcon sx={{ fontSize: 20 }} />
	                    </IconButton>

                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '18px',
                            overflow: 'hidden',
                            border: '1px solid rgba(255,255,255,0.08)',
                            bgcolor: '#000',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 1.25, sm: 1.75 },
                        }}
                    >
                        {lightboxCurrentItem?.isVideo ? (
                            <video
                                src={lightboxCurrentItem.url}
                                controls
                                preload="metadata"
                                playsInline
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                }}
                            />
                        ) : (
                            <img
                                src={lightboxCurrentItem?.url}
                                alt={title || 'Notícia'}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    objectPosition: 'center center',
                                    display: 'block',
                                }}
                            />
                        )}

                        {lightboxCount > 1 && (
                            <IconButton
                                onClick={goLightboxPrev}
                                disableRipple
                                sx={{
                                    position: 'absolute',
                                    left: navInset,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    ...navBtnSx,
                                }}
                                aria-label="Anterior"
                                title="Anterior"
                            >
                                <ArrowBackIcon sx={{ fontSize: isMobile ? 24 : 26 }} />
                            </IconButton>
                        )}

                        {lightboxCount > 1 && (
                            <IconButton
                                onClick={goLightboxNext}
                                disableRipple
                                sx={{
                                    position: 'absolute',
                                    right: navInset,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    ...navBtnSx,
                                }}
                                aria-label="Próximo"
                                title="Próximo"
                            >
                                <ArrowBackIcon sx={{ fontSize: isMobile ? 24 : 26, transform: 'rotate(180deg)' }} />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default NewsGallery;
